/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Conditions } from '@vsolv/packages/conditions/domain';
import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import { PropertyService } from '@vsolv/packages/properties/web';
import { DIMENSION_VALUE_NAMESPACE } from '@wsphere/warranties/domain';
import { Element, RatingMatrixService } from '../../services';
import {
  ConditionPickerProperty,
  getDimensionProperties,
  withDerivedAgeProperties,
} from '../condition-property-picker/condition-property-picker.component';

const DIMENSION_PREFIX = `${DIMENSION_VALUE_NAMESPACE}.`;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-condition-preview',
  templateUrl: './condition-preview.component.html',
  standalone: false,
})
export class ConditionPreviewComponent implements OnChanges {
  constructor(
    private propertySvc: PropertyService,
    private ratingMatrixSvc: RatingMatrixService
  ) {}

  @Input() propertySet!: PropertySet.Model;

  group: Conditions.ConditionGroup | null = null;
  @Input() set groupValue(group: Conditions.ConditionGroup | null) {
    this.group = group;
  }

  comparisons = [Conditions.Comparison.AND, Conditions.Comparison.OR];
  properties: (Property.Model<Property.PropertyType> | Conditions.Property)[] = [];

  text = Conditions.PropertyType.TEXT;

  // Elements for any categorical dimension referenced by a condition in this group,
  // keyed by dimension id — the stored condition value is the element id, not its title.
  private categoricalDimensionElements: Record<string, Element[]> = {};

  getConditions() {
    return this.group?.conditions.filter(cond => (cond as any)?.operator) as Conditions.Condition<
      string | number | boolean
    >[];
  }

  getGroups() {
    return this.group?.conditions.filter(cond => (cond as any)?.comparison) as Conditions.ConditionGroup[];
  }

  isCategoricalDimensionValue(property?: ConditionPickerProperty | null): property is ConditionPickerProperty {
    return !!property && property.type === this.text && property.valueKey.startsWith(DIMENSION_PREFIX);
  }

  getElementTitle(property: ConditionPickerProperty, elementId: unknown): string {
    const dimensionId = property.valueKey.slice(DIMENSION_PREFIX.length);
    const element = this.categoricalDimensionElements[dimensionId]?.find(el => el.id === elementId);
    return element?.title ?? String(elementId);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!changes['propertySet'] && !changes['groupValue']) return;

    const setProperties = withDerivedAgeProperties(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.propertySet.properties?.map(assignment => assignment.property!) ?? []
    );

    const dimensionEntries = this.propertySet?.id
      ? await getDimensionProperties(this.ratingMatrixSvc, this.propertySet.id)
      : [];

    this.properties = [...Conditions.getProperties(), ...setProperties, ...dimensionEntries];

    await this.loadCategoricalDimensionElements();
  }

  private async loadCategoricalDimensionElements() {
    const dimensionIds = new Set<string>();

    for (const condition of this.getConditions() ?? []) {
      const property = this.properties.find(p => p.valueKey === condition.propertyId);
      if (this.isCategoricalDimensionValue(property)) {
        dimensionIds.add(property.valueKey.slice(DIMENSION_PREFIX.length));
      }
    }

    await Promise.all(
      Array.from(dimensionIds).map(async dimensionId => {
        try {
          const res = await this.ratingMatrixSvc.listElements(dimensionId, { limit: 100 });
          this.categoricalDimensionElements[dimensionId] = res.items ?? [];
        } catch {
          this.categoricalDimensionElements[dimensionId] = [];
        }
      })
    );
  }
}
